import React, {ComponentProps, ReactElement, useMemo, useState} from 'react';

import {PowerCapacityId} from '../../../query/graphql';
import {useLargeScaleCalculator} from '../../decision/largeScaleCalculator';
import {SIMPLE_CALCULATOR_CROPS_NAMES, SimpleCalculatorCrop} from '../../decision/types';
import {POWER_CAPACITIES} from '../../resources/capacities';
import Select from '../Select';
import SimpleCalculatorCard from './SimpleCalculatorCard';
import SimpleCalculatorTable from './SimpleCalculatorTable';
import ValueWithUnit from './ValueWithUnit';

type Props = {};

const capacityOptions = Object.entries(POWER_CAPACITIES).map(([capacityId, capacity]) => ({
  label: capacity.description,
  value: capacityId,
}));

const cropOptions = Object.entries(SIMPLE_CALCULATOR_CROPS_NAMES).map(([cropId, name]) => ({
  label: name,
  value: cropId,
}));

const LargeScaleCalculator = ({}: Props): ReactElement => {
  const [capacityId, setCapacityId] = useState<PowerCapacityId>();
  const [cropId, setCropId] = useState<SimpleCalculatorCrop>();

  const input = {capacityId, cropId: cropId as SimpleCalculatorCrop | undefined};
  const capacityName = capacityId ? POWER_CAPACITIES[capacityId].description : 'Select';
  const cropName = cropId ? SIMPLE_CALCULATOR_CROPS_NAMES[cropId] : 'Select';

  const {data} = useLargeScaleCalculator(input);

  const values = useMemo<ComponentProps<typeof SimpleCalculatorTable>['values']>(
    () => [
      [
        {content: 'Capacity of power station', borderL: 'top'},
        {
          type: 'input',
          content: (
            <Select
              floating
              variant={'table'}
              buttonContent={capacityName}
              options={capacityOptions}
              selectedValues={capacityId ? [capacityId] : []}
              onOptionSelected={value => setCapacityId(value as PowerCapacityId)}
              buttonClassName={'rounded-tr-2xl'}
            />
          ),
          borderR: 'top',
        },
      ],
      [
        {content: 'Annual fuel requirement', borderL: 'bottom'},
        {content: <ValueWithUnit value={data?.fuelRequirement} unit={'tonnes'} />, borderR: 'bottom'},
      ],
      [
        {content: 'Crop choice', borderL: 'top'},
        {
          type: 'input',
          content: (
            <Select
              floating
              variant={'table'}
              buttonContent={cropName}
              options={cropOptions}
              selectedValues={cropId ? [cropId] : []}
              onOptionSelected={value => setCropId(value as SimpleCalculatorCrop)}
              buttonClassName={'rounded-tr-2xl'}
            />
          ),
          borderR: 'top',
        },
      ],
      [
        {content: 'Moisture content of feedstock when used', borderL: 'middle'},
        {content: <ValueWithUnit value={data?.inUseMC} unit={'%'} />, borderR: 'middle'},
      ],
      [
        {content: 'Typical generic yield of feedstock at required MC', borderL: 'bottom'},
        {content: <ValueWithUnit value={data?.genericYield} unit={'tonnes/ha'} />, borderR: 'bottom'},
      ],
      [{content: 'Area required'}, {content: <ValueWithUnit value={data?.areaRequired} unit={'ha'} />}],
      [{content: '% Land take within a 50km radius'}, {content: <ValueWithUnit value={data?.landTake} unit={'%'} />}],
    ],
    [
      capacityName,
      capacityId,
      data?.fuelRequirement,
      data?.inUseMC,
      data?.genericYield,
      data?.areaRequired,
      data?.landTake,
      cropName,
      cropId,
    ]
  );

  return (
    <SimpleCalculatorCard title={'Large Scale Use'}>
      <SimpleCalculatorTable values={values} />
    </SimpleCalculatorCard>
  );
};

export default LargeScaleCalculator;
